import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, Ref } from 'react';
import { useRef } from 'react';
import type { ListLinkItemsProps, ListLinkItemsRef } from './ListLinkItems.data';
import styles from './ListLinkItems.module.scss';
import {
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
  setupMouseEnterTimeline,
  setupMouseLeaveTimeline,
} from './ListLinkItems.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import Icon from '../../atoms/icon/Icon';
import Heading from '../../atoms/heading/Heading';
import Link from 'next/link';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import type { AhrefLink } from '../../../util/storyblok/types/global.types';

const Item = ({ item }: { item: AhrefLink }) => {
  const backgroundRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  const hoverTransitionController = useTransitionController(
    () => ({
      refs: {
        backgroundRef,
        iconRef,
      },
      setupTransitionInTimeline: setupMouseEnterTimeline,
      setupTransitionOutTimeline: setupMouseLeaveTimeline,
    }),
    [],
  );

  return (
    <Link
      href={item.link.cached_url || item.link.url}
      target={item.link.target}
      className={styles.item}
      onMouseEnter={() => hoverTransitionController.transitionIn()}
      onMouseLeave={() => hoverTransitionController.transitionOut()}
    >
      <div ref={backgroundRef} className={classNames(styles.background, 'abs-fill')} />
      <Heading
        transitionInType="splitWordsSlideInUp"
        className={classNames(styles.title)}
        as="h4"
        size="heading-04"
        disableTriggerInOnScroll={false}
      >
        {item.title}
      </Heading>
      <Icon ref={iconRef} className={styles.arrowRight} name="arrow-right" />
    </Link>
  );
};

export default function ListLinkItems({
  isVisible,
  className,
  blok,
  ref,
}: ListLinkItemsProps & { ref?: Ref<ListLinkItemsRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);
  useThemeScrollBlock(elementRef, blok.use_accent_color);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.listLinkItems, className)}
      {...sbEditable(blok)}
    >
      {blok.items.map((item) => (
        <Item key={item._uid} item={item} />
      ))}
    </div>
  );
}
